import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, useSyncExternalStore, type KeyboardEvent as ReactKeyboardEvent, type SyntheticEvent } from 'react'
import { createPortal } from 'react-dom'
import { FG_HELP_SHEET_MAX_WIDTH, computeFgHelpPosition, readOpenFgHelp, setOpenFgHelp, subscribeFgHelp, type FgHelpPosition } from './fgHelpStore'
import { REGISTRY_SOURCE } from './registry'
import type { HelpBody as HelpBodyModel, HelpSource } from './types'
import './FgHelp.css'

/** One help id, or several ids that describe the same spot (the first is primary, the rest render as related sections). */
export type FgHelpRef = string | readonly [string, ...string[]]

export type FgHelpProps = {
  id: string
  /** Additional entries written for exactly the same element; shown below the primary entry in one popover. */
  also?: readonly string[]
  /** `text` renders a labelled pill ("How this works") instead of the 17px "?" icon. */
  variant?: 'icon' | 'text'
  label?: string
  /** Where titles and bodies come from. Defaults to the namespaced registries in `./registry`. */
  source?: HelpSource
}

const stop = (event: SyntheticEvent) => { event.preventDefault(); event.stopPropagation() }
const FOCUSABLE = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
const INTERACTIVE = 'button, a[href], input, select, textarea, summary, label, [tabindex], [contenteditable="true"]'
/** Margin kept between the popover and every viewport edge; matches `computeFgHelpPosition`'s default. */
const EDGE = 12
/**
 * Characters of body text above which the popover opens at its wider size. At 380px three quarters of the
 * entries were taller than the room an anchor near mid-viewport leaves, and the longest showed a third of
 * themselves — so "Not covered" sat two scrolls below the fold on exactly the entries that need it read.
 */
const WIDE_CHARS = 900
const bodyLength = (entry?: HelpBodyModel) => entry ? entry.what.length + (entry.how ?? []).join('').length + (entry.systemRules ?? []).join('').length + (entry.limits ?? []).join('').length : 0

/** Renders an `FgHelpRef` (single id or id list) as one help button. */
export function FgHelpFor({ help, source }: { help?: FgHelpRef; source?: HelpSource }) {
  if (!help) return null
  return typeof help === 'string' ? <FgHelp id={help} source={source} /> : <FgHelp id={help[0]} also={help.slice(1)} source={source} />
}

export function FgHelp({ id, also = [], variant = 'icon', label, source = REGISTRY_SOURCE }: FgHelpProps) {
  const key = useId()
  const popoverId = `fg-help-${key.replace(/[^A-Za-z0-9_-]/g, '')}`
  const titleId = `${popoverId}-title`
  const open = useSyncExternalStore(subscribeFgHelp, readOpenFgHelp, () => null) === key
  const buttonRef = useRef<HTMLSpanElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const restoreFocus = useRef(false)
  const wasOpen = useRef(false)
  const mounted = useRef(true)
  const [content, setContent] = useState(() => source.loaded())
  const [loadFailed, setLoadFailed] = useState(false)
  const [container, setContainer] = useState<HTMLElement | null>(null)
  const [position, setPosition] = useState<FgHelpPosition | null>(null)
  const [sheet, setSheet] = useState(false)
  const sheetRevealed = useRef(false)
  const title = source.title(id) ?? id
  const wide = !!content && [id, ...also].reduce((total, key) => total + bodyLength(content[key]), 0) > WIDE_CHARS

  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false; if (readOpenFgHelp() === key) setOpenFgHelp(null) }
  }, [key])

  const load = useCallback(() => {
    const ready = source.loaded()
    if (ready) { setContent(ready); return }
    source.load().then(next => { if (mounted.current) { setContent(next); setLoadFailed(false) } }, () => { if (mounted.current) setLoadFailed(true) })
  }, [source])
  const close = useCallback((returnFocus: boolean) => {
    if (readOpenFgHelp() !== key) return
    restoreFocus.current = returnFocus
    setOpenFgHelp(null)
  }, [key])
  const toggle = (event: SyntheticEvent) => {
    // A help button can sit inside a <label>, <summary>, form or clickable row: cancelling the click keeps it
    // from toggling a checkbox, a disclosure or anything else its ancestors do.
    stop(event)
    if (open) { close(true); return }
    const button = buttonRef.current
    // Inside a modal dialog the popover must stay within it, or assistive technology treats it as inert.
    setContainer((button?.closest('[aria-modal="true"]') as HTMLElement | null) ?? document.body)
    setPosition(null); setSheet(false); setLoadFailed(false); sheetRevealed.current = false
    load()
    setOpenFgHelp(key)
  }

  // Focus moves into the popover when it opens and returns to the button when it is dismissed.
  useEffect(() => {
    if (open && !wasOpen.current) popoverRef.current?.focus({ preventScroll: true })
    if (!open && wasOpen.current) {
      if (restoreFocus.current) buttonRef.current?.focus({ preventScroll: true })
      restoreFocus.current = false
    }
    wasOpen.current = open
  }, [open])

  const reposition = useCallback(() => {
    const button = buttonRef.current, popover = popoverRef.current
    if (!button || !popover) return
    if (window.innerWidth <= FG_HELP_SHEET_MAX_WIDTH) { setSheet(true); setPosition(null); return }
    const anchor = button.getBoundingClientRect()
    // A non-modal popover must not outlive its anchor. Once the "?" has scrolled out of the viewport the
    // popover would hang over unrelated content with nothing to point at, so it closes instead of following.
    if (anchor.bottom < 0 || anchor.top > window.innerHeight) { close(false); return }
    const next = computeFgHelpPosition(anchor, { width: popover.offsetWidth, height: popover.scrollHeight }, { width: window.innerWidth, height: window.innerHeight })
    // Clamped at both ends: the bottom so the popover is not pushed below the fold, the top so a partly
    // scrolled-away anchor cannot drag its title off the top of the screen.
    next.top = Math.max(EDGE, Math.min(next.top, Math.max(EDGE, window.innerHeight - EDGE - Math.min(popover.scrollHeight, next.maxHeight))))
    setSheet(false)
    setPosition(old => old && old.top === next.top && old.left === next.left && old.maxHeight === next.maxHeight && old.placement === next.placement ? old : next)
  }, [close])
  useLayoutEffect(() => {
    if (!open) return
    reposition()
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => { window.removeEventListener('resize', reposition); window.removeEventListener('scroll', reposition, true) }
  }, [open, container, content, loadFailed, reposition])

  // At phone width the sheet covers most of the screen, so the control being explained, its current value and
  // its row are all behind it. Bring the anchor into the strip that stays above the sheet, once per opening.
  // It waits for the body: the first pass renders only "Loading help…", and measuring that short box would
  // make the sheet look small enough to be leaving the anchor visible when it is not.
  useLayoutEffect(() => {
    const button = buttonRef.current, popover = popoverRef.current
    if (!open || !sheet || !button || !popover || sheetRevealed.current || (!content && !loadFailed)) return
    sheetRevealed.current = true
    if (button.getBoundingClientRect().bottom > window.innerHeight - popover.offsetHeight - EDGE) button.scrollIntoView?.({ block: 'start' })
  }, [open, sheet, content, loadFailed])

  useEffect(() => {
    if (!open) return
    const onPointer = (event: Event) => {
      const target = event.target as Node | null
      if (!target || popoverRef.current?.contains(target) || buttonRef.current?.contains(target)) return
      const focusWasInside = !!popoverRef.current?.contains(document.activeElement)
      const interactive = target instanceof Element && !!target.closest(INTERACTIVE)
      close(false)
      // Clicking blank page space would otherwise leave keyboard focus on <body>.
      if (focusWasInside && !interactive) window.setTimeout(() => { if (readOpenFgHelp() === null && (!document.activeElement || document.activeElement === document.body)) buttonRef.current?.focus({ preventScroll: true }) }, 0)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault(); event.stopPropagation()
      close(!!popoverRef.current?.contains(document.activeElement) || document.activeElement === buttonRef.current || document.activeElement === document.body)
    }
    for (const type of ['pointerdown', 'mousedown', 'touchstart']) document.addEventListener(type, onPointer, true)
    document.addEventListener('keydown', onKey, true)
    return () => {
      for (const type of ['pointerdown', 'mousedown', 'touchstart']) document.removeEventListener(type, onPointer, true)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [open, close])

  const onPopoverKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation()
    if (event.key !== 'Tab') return
    const popover = event.currentTarget
    const focusables = Array.from(popover.querySelectorAll<HTMLElement>(FOCUSABLE))
    const active = document.activeElement
    // Non-modal: tabbing past either end closes the popover and resumes from the help button.
    if ((!event.shiftKey && (!focusables.length || active === focusables.at(-1))) || (event.shiftKey && (active === popover || active === focusables[0]))) {
      event.preventDefault()
      close(true)
    }
  }

  // The unplaced first render is positioned by the layout effect before the browser paints, so it is never
  // seen; it uses opacity rather than `visibility: hidden`, which would stop it being focusable.
  const popover = open && container ? createPortal(
    <div ref={popoverRef} id={popoverId} role="dialog" aria-labelledby={titleId} tabIndex={-1}
      className={`fg-help-popover${wide ? ' fg-help-wide' : ''}${sheet ? ' fg-help-sheet' : ''}`} data-placement={sheet ? 'sheet' : position?.placement}
      style={sheet ? undefined : position ? { top: position.top, left: position.left, maxHeight: position.maxHeight } : { top: 0, left: 0, opacity: 0 }}
      onClick={event => event.stopPropagation()} onKeyDown={onPopoverKeyDown}>
      <div className="fg-help-head">
        <h2 id={titleId}>{title}</h2>
        <button type="button" className="fg-help-close" aria-label="Close help" onClick={event => { stop(event); close(true) }}>
          <svg aria-hidden="true" focusable="false" viewBox="0 0 16 16"><path d="M4 4l8 8M12 4l-8 8" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
        </button>
      </div>
      {content ? <>
        <HelpBody entry={content[id]} level={3} />
        {also.map(related => <section className="fg-help-related" key={related} aria-label={source.title(related) ?? related}>
          {/* Named in words: without it a second entry under a rule-shaped heading reads as the same text repeated. */}
          <p className="fg-help-related-eyebrow">Also documented here</p>
          <h3 className="fg-help-related-title">{source.title(related) ?? related}</h3>
          <HelpBody entry={content[related]} level={4} />
        </section>)}
      </> : loadFailed
        ? <p className="fg-help-status" role="alert">Help could not be loaded. <button type="button" className="fg-help-retry" onClick={event => { stop(event); setLoadFailed(false); load() }}>Try again</button></p>
        : <p className="fg-help-status" role="status">Loading help…</p>}
    </div>, container) : null

  // Enter activates on key-down and Space on key-up, the way a native button does. Both are swallowed so a
  // surrounding <summary>, label or row never sees them.
  const activationKey = (event: ReactKeyboardEvent<HTMLSpanElement>, activateOn: string) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.stopPropagation()
    event.preventDefault()
    if (event.key === activateOn) toggle(event)
  }
  const text = variant === 'text'
  // Deliberately a span rather than a <button>: a <button> inside a `<fieldset disabled>` is itself disabled,
  // which would kill the help on every locked form — exactly when a reader most needs it — and a <button>
  // inside a <label> is invalid content. `role`, `tabIndex` and the key handlers restore button behaviour.
  return <>
    <span ref={buttonRef} role="button" tabIndex={0} className={`fg-help-button${text ? ' fg-help-text' : ''}`}
      data-help-id={id} data-help-also={also.length ? also.join(' ') : undefined}
      aria-label={text && label ? `${label}. Help: ${title}` : `Help: ${title}`} aria-expanded={open} aria-controls={popoverId} aria-haspopup="dialog"
      onClick={toggle} onPointerEnter={load} onFocus={load}
      onKeyDown={event => activationKey(event, 'Enter')} onKeyUp={event => activationKey(event, ' ')}>
      <svg aria-hidden="true" focusable="false" viewBox="0 0 16 16"><path d="M5.7 6.1a2.35 2.35 0 1 1 3.4 2.1c-.72.38-1.1.92-1.1 1.62v.28" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /><circle cx="8" cy="12.75" r="1.05" fill="currentColor" /></svg>
      {text && label && <span>{label}</span>}
    </span>
    {popover}
  </>
}

function HelpBody({ entry, level }: { entry: HelpBodyModel | undefined; level: 3 | 4 }) {
  if (!entry) return null
  const Heading = level === 3 ? 'h3' : 'h4'
  return <>
    <p className="fg-help-what">{entry.what}</p>
    {!!entry.how?.length && <><Heading>How to use it</Heading><ol>{entry.how.map((step, i) => <li key={i}>{step}</li>)}</ol></>}
    {!!entry.systemRules?.length && <><Heading>What the system does</Heading><ul>{entry.systemRules.map((rule, i) => <li key={i}>{rule}</li>)}</ul></>}
    {!!entry.limits?.length && <><Heading>Not covered</Heading><ul>{entry.limits.map((limit, i) => <li key={i}>{limit}</li>)}</ul></>}
  </>
}
